import { Box, Dialog, DialogContent, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { useMemo, useState } from "react"

import useInstructionActions from "../../hooks/useInstructionActions"
import DialogArea from "../addProduct/DialogArea"
import ProductsArea from "../addProduct/ProductsArea"

import ProductSearchSuggestion from "@/components/SearchSuggestion/ProductSearchSuggestion"
import type { AerosTagData } from "@/components/UiKit"
import type { Product } from "@/graphql/codegen/graphql"
import useParametrizedProductsForInstructionList from "@/screens/ManageProducts/components/Instructions/hooks/useParametrizedProductsForInstructionList"
import useProductTableActionsOptions from "@/screens/ManageProducts/components/Instructions/hooks/useProductTableActionsOptions"
import SelectedItemsArea from "@/screens/Products/components/Product/Instructions/components/SelectedItemsArea/SelectedItemsArea"
import type { AutocompleteOption } from "@/types/form-fields"

interface AddProductDialogProps {
  instructionId: string
  instructionName: string
  associatedProductIds: number[]
  onClose: () => void
  onSuccess: () => void
}

const AddProductDialog = ({
  associatedProductIds,
  instructionId,
  instructionName,
  onClose,
  onSuccess,
}: AddProductDialogProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState<string | AutocompleteOption>("")

  const [showInactive, setShowInactive] = useState<boolean>(false)

  const onSearch = (searchedItem: string | AutocompleteOption) => {
    setSearchQuery(searchedItem)
    // if searchQuery is an exact product selected in suggestion list the system automatically flips that to Show Inactive Products
    if (searchedItem && typeof searchedItem !== "string") {
      setShowInactive(true)
    }
  }

  const { productsToAssociate, selected, setSelected, selectedProducts } = useProductTableActionsOptions({
    associatedProductIds,
  })

  const { addProductsToInstruction } = useInstructionActions()

  const { products, fetchingProducts, noProductsFoundMessage } = useParametrizedProductsForInstructionList({
    searchQuery,
    showInactive,
  })

  const handleSubmit = async () => {
    const success = await addProductsToInstruction({
      instructionId,
      productIds: productsToAssociate,
    })
    if (success) {
      onSuccess()
    }
  }

  // Only newly added products are displayed in selected area and can be removed
  const selectedTags = useMemo(() => {
    const list = [...products, ...selectedProducts.current]
    const newListOfSelectedProducts: Product[] = []
    const newTags = productsToAssociate.flatMap((id) => {
      const found = list.find((i) => i.productId === id)
      if (!found) {
        return []
      }
      newListOfSelectedProducts.push(found)
      const { productId, companyProductDescription } = found
      return [
        {
          key: productId,
          value: productId,
          label: companyProductDescription!,
        },
      ]
    })

    // Here save selected product data as with a new query we may lose it coz new products are fetched
    selectedProducts.current = newListOfSelectedProducts

    return newTags
  }, [products, productsToAssociate, selectedProducts])

  const onRemoveSelectedProduct = (tagToDelete: AerosTagData) => {
    const idToRemove = tagToDelete.value as number

    selectedProducts.current = selectedProducts.current.filter((i) => i.productId !== idToRemove)

    setSelected((prev) => {
      const { [idToRemove]: _, ...newList } = prev
      return newList as Record<string, boolean>
    })
  }

  const instructionLabel = instructionName ? `"${instructionName}"` : ""
  const dialogTitle = `Associate Products to your Instruction ${instructionLabel}`

  return (
    <Dialog
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            maxWidth: "1072px",
            width: "100%",
          },
        },
      }}
    >
      <DialogArea
        title={dialogTitle}
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitEnabled={!!productsToAssociate.length}
        submitText='Associate'
      >
        <DialogContent classes={{ root: "p-10" }}>
          {!!productsToAssociate.length && (
            <SelectedItemsArea items={selectedTags} onDelete={onRemoveSelectedProduct} title='Selected Products' />
          )}
          <Typography className='mb-6 text-ml font-normal text-gray-900'>Look for a Product to Associate </Typography>
          <Box className='flex flex-col gap-6'>
            <ProductSearchSuggestion onSearch={onSearch} />
            {searchQuery && (
              <ProductsArea
                products={products}
                fetchingProducts={fetchingProducts}
                noProductsFoundMessage={noProductsFoundMessage}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                selected={selected}
                onProductSelection={setSelected}
                associatedProductIds={associatedProductIds}
              />
            )}
          </Box>
        </DialogContent>
      </DialogArea>
    </Dialog>
  )
}

export default AddProductDialog
